import { useState } from "react";
import { toast } from "sonner";
import { Loader2, MessageSquare } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, formatApiError, fmtDate } from "@/lib/api";

export const STATUS_TEXT = { open: "Waiting for our team", answered: "Team replied", closed: "Closed" };

export const NewMessageDialog = ({ policies, onDone }) => {
  const [open, setOpen] = useState(false);
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [policyId, setPolicyId] = useState("none");
  const [busy, setBusy] = useState(false);
  const send = async () => {
    setBusy(true);
    try {
      await api.post("/support", { subject, message, policy_id: policyId === "none" ? null : policyId });
      toast.success("Message sent to our team");
      setOpen(false);
      setSubject("");
      setMessage("");
      onDone();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button variant="outline" size="sm" data-testid="new-message-btn"><MessageSquare className="mr-1 h-4 w-4" /> Message our team</Button></DialogTrigger>
      <DialogContent data-testid="new-message-dialog">
        <DialogHeader>
          <DialogTitle>Message our team</DialogTitle>
          <DialogDescription>For cancellations, coverage changes, refunds or anything else. We reply here in your account.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-4">
          <div className="space-y-1.5"><Label>About a policy (optional)</Label>
            <Select value={policyId} onValueChange={setPolicyId}>
              <SelectTrigger data-testid="message-policy-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">General question</SelectItem>
                {policies.map((p) => <SelectItem key={p.id} value={p.id}>{p.policy_number}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5"><Label htmlFor="m-sub">Subject</Label><Input id="m-sub" value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="message-subject-input" /></div>
          <div className="space-y-1.5"><Label htmlFor="m-body">Message</Label><Textarea id="m-body" rows={4} value={message} onChange={(e) => setMessage(e.target.value)} data-testid="message-body-input" /></div>
          <Button disabled={busy || subject.trim().length < 3 || message.trim().length < 5} onClick={send} className="bg-blue-700 text-white hover:bg-blue-800" data-testid="message-send-btn">
            {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Send
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const Thread = ({ r, mine = "customer" }) => (
  <div className="space-y-2" data-testid={`thread-${r.id}`}>
    {r.messages.map((m, i) => (
      <div key={i} className={`flex ${m.sender === mine ? "justify-end" : "justify-start"}`}>
        <div className={`max-w-[85%] whitespace-pre-wrap rounded-xl px-3 py-2 text-sm ${m.sender === mine ? "bg-blue-700 text-white" : "bg-slate-100"}`}>
          <p className="mb-0.5 text-[11px] opacity-70">{m.sender === "team" ? "Seutrade team" : r.user_name} · {fmtDate(m.at)}</p>{m.text}
        </div>
      </div>
    ))}
  </div>
);

const RequestCard = ({ r, onDone }) => {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const reply = async () => {
    try {
      await api.post(`/support/${r.id}/reply`, { message: text });
      setText("");
      onDone();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4" data-testid={`support-card-${r.id}`}>
      <button className="flex w-full items-center justify-between gap-3 text-left" onClick={() => setOpen(!open)} data-testid={`support-toggle-${r.id}`}>
        <span><span className="font-semibold">{r.subject}</span><span className="block text-xs text-slate-500">{r.policy_number && `Policy ${r.policy_number} · `}Updated {fmtDate(r.updated_at)}</span></span>
        <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${r.status === "answered" ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-600"}`} data-testid={`support-status-${r.id}`}>{STATUS_TEXT[r.status]}</span>
      </button>
      {open && (
        <div className="mt-4 space-y-3">
          <Thread r={r} />
          {r.status !== "closed" && (
            <div className="flex gap-2">
              <Input value={text} onChange={(e) => setText(e.target.value)} placeholder="Write a reply…" data-testid={`support-reply-input-${r.id}`} />
              <Button disabled={!text.trim()} onClick={reply} data-testid={`support-reply-btn-${r.id}`}>Send</Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export const SupportList = ({ requests, onDone }) => (
  <div className="grid gap-3" data-testid="support-list">{requests.map((r) => <RequestCard key={r.id} r={r} onDone={onDone} />)}</div>
);
